import * as React from 'react';
import { ExclamationCircleFilled } from '@ant-design/icons';
import cx from 'classnames';
import {
  debounce, filter, isArray, isEmpty, isNull, map,
} from 'lodash';
import moment from 'moment';
import { ColumnDef } from '@tanstack/react-table';
import {
  IRowData, LoadSpinner,
} from '@components';

import { OFFER_SOURCE } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import { GET_MEMBERS_BY_COMMUNITY_QUERY } from '@frontend/app/queries/getMembersByCommunityQuery';
import { MAX_ALLOWED_MEMBERS } from '@frontend/applications/AffiliatesApp/utils';
import { useState } from 'react';
import _ from 'lodash';
import { Columns, Search } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@frontend/shadcn/components/ui/select';
import { Input } from '@frontend/shadcn/components/ui/input';
import { DataTable, TablePagination } from '@frontend/app/refresh-components/DataTableV2';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@frontend/shadcn/components/ui/tooltip';
import {
  TooltipHeader,
  TooltipTitle,
} from '@frontend/applications/Social/components/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@frontend/shadcn/components/ui/popover';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import ShadCnAlert from '@frontend/app/refresh-components/Alert';
import { GET_COMMUNITIES_QUERY } from '@frontend/app/queries/GetCommunitiesQuery';
import { useApolloClientForAspirex } from '@affiliates/hooks/useApolloClientForAspirex';
import { logger } from '@common';
import { useOfferDetailsContext } from '@frontend/context/OfferDetailsContext';
import {
  cleanPromoCode, getMemberOfferStatus, isOfferShopify, isOfferTune,
} from '../../utils';
import {
  ACTIVE_DATE_FORMAT, ACTIVE_TIME_FORMAT, TMember, TMode, TProgram, TSelectedMember,
} from '../../types';

interface TCommunity {
  id: number;
  title: string;
  memberCount: number;
}

interface TCommunityMember {
  id: number;
  name: string;
  email: string;
  instagramUsername: string;
}

import styles from '../MembersWizard.scss';

const { useEffect, useMemo } = React;

interface IBaseProps {
  markMembersSelected: (members: readonly TSelectedMember[]) => void;
  members: readonly TSelectedMember[] | null;
  mode: TMode;
  offerSource: OFFER_SOURCE;
  isNewFlow?: boolean;
  isWorkflow?: boolean;
  fetchMembers?: (
    offset?: number,
    limit?: number,
    searchByName?: string,
    programs?: number[],
    isNewFlow?: boolean,
  ) => void;
  fetchMembersCount?: (programIds: number[], searchByName?: string) => Promise<number>;
  programId?: number[];
  programs?: readonly TProgram[] | null;
  selectedPrograms?: {
    [programId: string]: TProgram;
  };
  markProgramsSelected?: (programs: readonly TProgram[]) => void;
  updateSearchKey?: (searchKey: string) => void;
  searchKey?: string;
  resetMember?: () => void;
  isLoading?: boolean;
  updateIsLoading?: (isLoading: boolean) => void;
  populateMembers?: (members: readonly TMember[]) => void;
}

interface IAddProps extends IBaseProps {
  mode: 'add';
  noMembersWarning: React.ReactElement;
  updateMemberCode: (memberId: string, field: string, value: string) => void;
  offerLink?: string;
  updateMemberDeepLink: (memberId: string, deepLink: string, focused: boolean) => void;
  isWorkflow: boolean;
  deepLinkColumn: boolean;
}
interface IRefreshProps extends IBaseProps {
  mode: 'refresh';
  offerName: string;
  refreshDatesOnly: boolean;
  updateMemberCode: (memberId: string, field: string, value: string) => void;
}
interface IDeactivateProps extends IBaseProps {
  mode: 'deactivate';
  offerName: string;
}

type TProps = Readonly<IAddProps | IDeactivateProps | IRefreshProps>;

const getSelectedMemberSizeAlert = (
  members: TProps['members'],
  offerSource: OFFER_SOURCE,
  isNewFlow: boolean,
): React.ReactElement => {
  const selectedMember = filter(members, (m) => m.selected).length;
  if (offerSource === OFFER_SOURCE.TUNE || (offerSource === OFFER_SOURCE.SHOPIFY && !isNewFlow)) {
    return null;
  }
  if (selectedMember <= 200) {
    return null;
  }
  const message = 'Please select a maximum of 200 members at a time to generate the promo codes'.trim();
  return <ShadCnAlert
    title="Too Many Members"
    description={message}
    type="info"
  />;
};

export function normalizeName(name) {
  // Normalize to decompose combined characters
  // Remove diacritical marks and convert to plain text
  const normalizedName = name
    .normalize('NFKD') // Decompose combined characters
    .replace(/\p{M}/gu, '') // Remove all combining marks (diacritical marks)
    .replace(/[^\p{L}\p{N}\s._]/gu, '')// Allow letters, numbers, whitespace, underscores, and dots
    .toLowerCase();
  return normalizedName;
}
const getDuplicateCodesAlert = (members): React.ReactElement => {
  const duplicateCodeCount = filter(members, (m) => m.selected && m.isDuplicate).length;
  if (duplicateCodeCount < 1) {
    return null;
  }
  const message = `
    ${duplicateCodeCount.toLocaleString()} members will have the same code.
    Each member must have a unique promo code so Shopify can properly
    attribute conversion data to the member.
  `.trim();
  return (
    <ShadCnAlert
      title="Duplicate Code"
      description={message}
      type="error"
    />
  );
};
export const RefreshSelectMembers: React.FC<TProps> = (props) => {
  const {
    markMembersSelected,
    members,
    offerSource,
    fetchMembersCount,
    isNewFlow,
    programs,
    selectedPrograms,
    markProgramsSelected,
    isWorkflow,
    searchKey,
    updateSearchKey,
    resetMember,
    populateMembers,
  } = props;

  // State for community filtering
  const [selectedCommunities, setSelectedCommunities] = useState<{ [communityId: string]: TCommunity }>({});
  const [communitiesData, setCommunitiesData] = useState<{ communities: TCommunity[] } | null>(null);
  const [communityMembers, setCommunityMembers] = useState<TCommunityMember[]>([]);
  const [loadingCommunityMembers, setLoadingCommunityMembers] = useState(false);
  const [communityFilterActive, setCommunityFilterActive] = useState(false);
  const { offerMembers = [] } = useOfferDetailsContext() || {};

  // Apollo clients
  const aspirexClient = useApolloClientForAspirex();

  // fetch members
  const [totalMember, setTotalMember] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const isShopify = isOfferShopify(offerSource);
  const isTune = isOfferTune(offerSource);
  const pageSize = MAX_ALLOWED_MEMBERS;
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    instagramUsername: true
  });

  // Fetch communities on component mount
  useEffect(() => {
    const fetchCommunities = async () => {
      try {
        const result = await aspirexClient.query({
          query: GET_COMMUNITIES_QUERY,
          fetchPolicy: 'cache-first',
        });
        setCommunitiesData(result.data);
      } catch (error) {
        setCommunitiesData({ communities: [] });
      }
    };

    fetchCommunities();
  }, [aspirexClient]);
  // Member Count
  useEffect(() => {
    if (props.mode !== 'add') {
      return;
    }
    if (isWorkflow) {
      setTotalMember(members ? members.length : 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, isWorkflow]);
  useEffect(() => {
    if (props.mode !== 'add') {
      return;
    }

    const delayCountFetch = setTimeout(() => {
      if (!isWorkflow) {
        props.updateIsLoading(true);
        fetchMembersCount(props.programId, searchKey)
          .then((count) => {
            setTotalMember(count);
          })
          .catch((error) => {
            logger.error('Error fetching total member count:', error);
          })
          .finally(() => {
            props.updateIsLoading(false);
          });
      }
    }, 500);
    return () => clearTimeout(delayCountFetch);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMembersCount, searchKey, selectedPrograms, isWorkflow]);
  useEffect(() => {
    if (props.mode !== 'add' || !isNull(members)) {
      return;
    }

    props?.fetchMembers(currentPage, pageSize, searchKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.mode]);

  useEffect(() => {
    if (props.mode !== 'add' || isWorkflow) {
      return;
    }

    props?.fetchMembers(currentPage, pageSize, searchKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);
  useEffect(() => {
    if (props.mode !== 'add' || isWorkflow) {
      return;
    }

    const searchMember = debounce(() => {
      const controller = new AbortController();
      props?.fetchMembers(currentPage, pageSize, searchKey, undefined, undefined);
      return () => controller.abort();
    }, 200);

    searchMember();

    return () => {
      searchMember.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKey]);
  const data = useMemo(() => {
    if (isNull(members)) {
      return [];
    }
    return map(
      members,
      (m): IRowData => {
        let errorMessage = '';
        if (m.selected) {
          if (props.mode === 'add' && isEmpty(m.code)) {
            errorMessage = 'Manual entry needed due to missing member details.';
          } else if (m.isDuplicate) {
            errorMessage = 'Code must be unique.';
          }
        }

        switch (props.mode) {
          case 'add': {
            let code = null;
            if (!m.forceCheckIn) {
              code = {
                code: m.code,
                errorMessage,
                onChange: (event) => {
                  const input = event.target;
                  const cursor = input.selectionStart;
                  const preCursor = cleanPromoCode(input.value.slice(0, cursor));
                  const postCursor = cleanPromoCode(input.value.slice(cursor));
                  const code = `${preCursor}${postCursor}`;
                  if (code !== m.code) {
                    props.updateMemberCode(m.id.toString(), 'code', code);
                  }
                  setTimeout(() => {
                    input.selectionStart = preCursor.length;
                    input.selectionEnd = preCursor.length;
                  }, 0);
                },
              };
            }
            return {
              code,
              id: m.id.toString(),
              key: m.id.toString(),
              isDuplicate: m.selected && m.isDuplicate,
              name: m.name,
              instagramUsername: m.instagramUsername,
              offerStatus: getMemberOfferStatus(m, props.isWorkflow, offerSource),
              programs: m.selectedPrograms,
              _bodyRowClassName: cx({
                [styles.codeHasError]: m.selected && (isEmpty(m.code) || m.isDuplicate),
              }),
              disableSelection: m.forceCheckIn,
              deepLink: { ...m.deepLink, memberId: m.id.toString() },
              _raw: m,
            };
          }
          case 'refresh': {
            let code = null;
            if (!m.forceCheckIn) {
              code = {
                code: m.code,
                errorMessage,
                onChange: (event) => {
                  const input = event.target;
                  const cursor = input.selectionStart;
                  const preCursor = cleanPromoCode(input.value.slice(0, cursor));
                  const postCursor = cleanPromoCode(input.value.slice(cursor));
                  const code = `${preCursor}${postCursor}`;
                  if (code !== m.code) {
                    props.updateMemberCode(m.id.toString(), 'code', code);
                  }
                  setTimeout(() => {
                    input.selectionStart = preCursor.length;
                    input.selectionEnd = preCursor.length;
                  }, 0);
                },
              };
            }
            return {
              code,
              id: m.id.toString(),
              key: m.id.toString(),
              isDuplicate: m.selected && m.isDuplicate,
              name: m.name,
              offerName: m.forceCheckIn ? getMemberOfferStatus(m, props.isWorkflow, offerSource) : props.offerName,
              previousCode: m.previousCode.code,
              previousCodeEndDate: moment(m.previousCode.end).format(`${ACTIVE_DATE_FORMAT}, ${ACTIVE_TIME_FORMAT}`),
              previousCodeStartDate: moment(m.previousCode.start).format(
                `${ACTIVE_DATE_FORMAT}, ${ACTIVE_TIME_FORMAT}`,
              ),
              _bodyRowClassName: cx({
                [styles.codeHasError]: m.selected && m.isDuplicate,
              }),
              disableSelection: m.forceCheckIn,
              _raw: m,
            };
          }
          case 'deactivate': {
            return {
              code: m.code,
              id: m.id.toString(),
              key: m.id.toString(),
              name: m.name,
              offerName: props.offerName,
              previousCode: m.previousCode.code,
              previousCodeEndDate: moment(m.previousCode.end).format(ACTIVE_DATE_FORMAT),
              previousCodeStartDate: moment(m.previousCode.start).format(ACTIVE_DATE_FORMAT),
              programs: m.selectedPrograms,
              _raw: m,
            };
          }
        }
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members]);

  // Handle totalMember state updates separately from filteredData to avoid side effects in useMemo
  useEffect(() => {
    if (communityFilterActive) {
      if (communityMembers.length === 0) {
        setTotalMember(0);
      } else {
        // Filter out members that are already in the offer
        const offerMemberIds = new Set((offerMembers || []).map((offerMember) => offerMember.memberId));
        const availableCommunityMembers = communityMembers.filter((member) => !offerMemberIds.has(member.id));

        // Calculate filtered community data length for search
        let filteredLength = availableCommunityMembers.length;
        if (searchKey) {
          const filteredCommunityData = _.filter(availableCommunityMembers, (member) =>
            _.includes(normalizeName(member.name.toLowerCase()), searchKey.toLowerCase()));
          filteredLength = filteredCommunityData.length;
        }
        setTotalMember(filteredLength);
      }
    } else if (!searchKey && !isWorkflow) {
      // Keep existing totalMember for non-workflow, non-search scenarios
      // This will be set by the fetchMembersCount effect
    } else if (props.isWorkflow) {
      // For workflow mode, count filtered data
      let filteredLength = data.length;
      if (searchKey) {
        const filteredDataForCount = _.filter(data, (item) =>
          _.includes(normalizeName(item.name.toLowerCase()), searchKey.toLowerCase()));
        filteredLength = filteredDataForCount.length;
      }
      setTotalMember(filteredLength);
    }
  }, [communityFilterActive, communityMembers, searchKey, isWorkflow, props.isWorkflow, data, offerMembers]);

  const filteredData = useMemo(() => {
    // If community filter is active, show community members directly (including empty results)
    if (communityFilterActive) {
      if (communityMembers.length === 0) {
        return [];
      }

      // Filter out members that are already in the offer
      const offerMemberIds = new Set((offerMembers || []).map((offerMember) => offerMember.memberId));
      const availableCommunityMembers = communityMembers.filter((member) => !offerMemberIds.has(member.id));

      // Convert community members to the expected table format
      const communityMemberData = availableCommunityMembers.map((member): IRowData => {
        const memberName = member.name || 'Unknown Member';
        return {
          id: member.id.toString(),
          key: member.id.toString(),
          name: memberName,
          instagramUsername: member.instagramUsername || '', // Use email as placeholder
          offerStatus: 'Not in Offer', // Default status
          programs: '', // No program data for community members
          isDuplicate: false,
          disableSelection: false,
          _raw: {
            id: member.id,
            name: memberName,
            email: member.email,
            selected: false,
            code: '',
            isDuplicate: false,
          },
          // Add required fields for different modes
          ...(props.mode === 'add' && {
            code: null, // Community members don't have codes yet
          }),
          ...(props.mode === 'refresh' && {
            offerName: 'Not in Offer',
            previousCode: '',
            previousCodeEndDate: '',
            previousCodeStartDate: '',
          }),
          ...(props.mode === 'deactivate' && {
            offerName: 'Not in Offer',
            previousCode: '',
            previousCodeEndDate: '',
            previousCodeStartDate: '',
          }),
        };
      });

      // Apply search filter to community members
      let filteredCommunityData = communityMemberData;
      if (searchKey) {
        filteredCommunityData = _.filter(communityMemberData, (item) =>
          _.includes(normalizeName(item.name.toLowerCase()), searchKey.toLowerCase()));
      }

      return filteredCommunityData;
    }

    // Default behavior: show offer members (when no community filter)
    let filteredBySearch = data;

    // Apply search filter to offer members
    if (searchKey && (props.isWorkflow || !isWorkflow)) {
      filteredBySearch = _.filter(data, (item) =>
        _.includes(normalizeName(item.name.toLowerCase()), searchKey.toLowerCase()));
    }

    return filteredBySearch;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchKey, props.isWorkflow, communityFilterActive, communityMembers, offerMembers]);

  // Calculate selected member count including community members when community filtering is active
  const selectedMemberCount = useMemo(() => {
    if (communityFilterActive) {
      // When community filtering is active, count selected community members
      const selectedCommunityRows = filteredData.filter((row) => rowSelection[row.id]);
      return selectedCommunityRows.length;
    } else {
      // When not filtering by community, count selected offer members
      return isArray(members) ? filter(members, (m) => m.selected).length : 0;
    }
  }, [communityFilterActive, filteredData, rowSelection, members]);

  const pagedData = useMemo(() => {
    return filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }, [filteredData, currentPage, pageSize]);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (updateSearchKey) {
      updateSearchKey(e.target.value);
      setCurrentPage(1);
    }
  };

  const columns = useMemo(() => {
    if (props.mode === 'add') {
      const baseColumns = [
        {
          id: 'name',
          accessorKey: 'name',
          header: 'Name',
          cell: ({ row }) => (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="truncate w-[120px]">
                  {row.original.name.length > 15
                    ? `${row.original.name.substring(0, 15)}...`
                    : row.original.name}
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-[9999]">
                <span>{row.original.name}</span>
              </TooltipContent>
            </Tooltip>
          ),
          size: 200,
          minSize: 200,
          maxSize: 200,
        },
        {
          id: 'offerStatus',
          accessorKey: 'offerStatus',
          header: 'Offer',
          cell: ({ row }) => row.original.offerStatus,
          size: 200,
          minSize: 200,
          maxSize: 200,
        },
        {
          id: 'instagramUsername',
          accessorKey: 'instagramUsername',
          header: 'Instagram',
          cell: ({ row }) => (
            <div data-testid="instagramUsername-column-checkbox">
              {row.original.instagramUsername || '-'}
            </div>
          ),
          size: 200,
          minSize: 200,
          maxSize: 200,
        },
      ];

      return baseColumns;
    }

    if (props.mode === 'refresh') {
      if (isShopify) {
        const columns = [
          {
            accessorKey: 'name',
            header: 'Member',
            cell: ({ row }) => (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="truncate w-[120px]">
                    {row.original.name.length > 15
                      ? `${row.original.name.substring(0, 15)}...`
                      : row.original.name}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-[9999]">
                  <TooltipHeader>
                    <TooltipTitle>{row.original.name}</TooltipTitle>
                  </TooltipHeader>
                </TooltipContent>
              </Tooltip>
            ),
            size: 200,
            minSize: 200,
            maxSize: 200,
          },
          {
            accessorKey: 'previousCode',
            header: props.refreshDatesOnly ? 'Active Code' : 'Previous Code',
            cell: ({ row }) => (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="truncate w-[120px]" data-testid={`previous-code-${row.original.id}`}>
                    {row.original?.previousCode?.length > 15
                      ? `${row.original?.previousCode?.substring(0, 15)}...`
                      : row.original?.previousCode}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">
                  <TooltipHeader>
                    <TooltipTitle>{row.original.previousCode}</TooltipTitle>
                  </TooltipHeader>
                </TooltipContent>
              </Tooltip>
            ),
            size: 200,
            minSize: 200,
            maxSize: 200,
          },
          {
            accessorKey: 'offerName',
            header: 'Offer',
            cell: ({ row }) => (
              <div data-testid={`refresh-mode-offer-${row.original.id}`}>
                {row.original.offerName}
              </div>
            ),
            size: 200,
            minSize: 200,
            maxSize: 200,
          },
          {
            id: 'instagramUsername',
            accessorKey: 'instagramUsername',
            header: 'Instagram',
            cell: ({ row }) => (
              <div data-testid={`instagram-username-${row.original.id}`}>
                {row.original.instagramUsername || '-'}
              </div>
            ),
            size: 200,
            minSize: 200,
            maxSize: 200,
          },
        ];

        if (props.refreshDatesOnly) {
          columns.splice(1, 0,
            {
              accessorKey: 'previousCodeStartDate',
              header: 'Start Date',
              cell: ({ row }) => row.original.previousCodeStartDate,
              size: 150,
              minSize: 150,
              maxSize: 150,
            },
            {
              accessorKey: 'previousCodeEndDate',
              header: 'End Date',
              cell: ({ row }) => row.original.previousCodeEndDate,
              size: 150,
              minSize: 150,
              maxSize: 150,
            }
          );
        } else {
          columns.splice(1, 0, {
            accessorKey: 'code',
            header: 'Code',
            cell: ({ row }) => {
              const data = row.original.code;
              if (!data) return null;
              return (
                <div className="flex items-center gap-2">
                  <Input
                    className={cx(
                      {
                        '!border-red-4 focus-visible:ring-red-4': data.errorMessage
                      }
                    )}
                    onChange={data.onChange}
                    placeholder="TYPE CODE HERE"
                    value={data.code}
                  />
                  {data.errorMessage && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <ExclamationCircleFilled className="!text-red-4" />
                      </TooltipTrigger>
                      <TooltipContent
                        className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[270px]"
                        side="top"
                        align="end"
                      >
                        <TooltipHeader>
                          <TooltipTitle>{data.errorMessage}</TooltipTitle>
                        </TooltipHeader>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              );
            },
            size: 200,
            minSize: 200,
            maxSize: 200,
          });
        }
        return columns;
      }

      if (isTune) {
        return [
          {
            accessorKey: 'name',
            header: 'Member',
            cell: ({ row }) => (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="truncate w-[120px]">
                    {row.original.name.length > 15
                      ? `${row.original.name.substring(0, 15)}...`
                      : row.original.name}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-[9999]">
                  <TooltipHeader>
                    <TooltipTitle>{row.original.name}</TooltipTitle>
                  </TooltipHeader>
                </TooltipContent>
              </Tooltip>
            ),
            size: 300,
            minSize: 300,
            maxSize: 300,
          },
          {
            accessorKey: 'offerName',
            header: 'Offer',
            cell: ({ row }) => row.original.offerName,
            size: 300,
            minSize: 300,
            maxSize: 300,
          }
        ];
      }
    }

    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShopify, isTune, props.isNewFlow, props.mode]);

  useEffect(() => {
    const selectedRows = filteredData.filter((row) => rowSelection[row.id]);

    if (communityFilterActive && selectedRows.length > 0 && populateMembers) {
      // When community filtering is active and members are selected,
      // we need to add them to the state first using populateMembers
      const communityMembersToAdd = selectedRows.map((row) => ({
        id: row._raw.id,
        name: row._raw.name,
        firstName: row._raw.name.split(' ')[0] || '',
        lastName: row._raw.name.split(' ').slice(1).join(' ') || '',
        email: row._raw.email,
        instagramUsername: row._raw.instagramUsername,
        programIds: [], // No program data for community members
        affiliateOfferId: 0,
        inOffer: false,
        forceCheckIn: false,
        previousCode: { code: '', start: new Date(), end: new Date() },
        affiliateId: undefined,
        payoutId: 1, // Default payout ID
      }));

      // First add community members to the state
      populateMembers(communityMembersToAdd);

      // Then mark them as selected after a short delay to ensure state is updated
      setTimeout(() => {
        const selectedMembers = selectedRows.map((row) => ({
          ...row._raw,
          selected: true,
          code: row._raw.code || '',
          isDuplicate: false,
          selectedPrograms: '',
          payoutId: 1,
          startDate: null,
          endDate: null,
        }));
        markMembersSelected(selectedMembers);
      }, 100);
    } else {
      // For non-community members, just mark as selected
      const selectedMembers = selectedRows.map((row) => row._raw);
      markMembersSelected(selectedMembers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection, communityFilterActive]);

  // Initialize rowSelection when isWorkflow is true (but not for community filtering)
  // Only initialize on first load, not when user deselects all items
  const [hasInitializedWorkflowSelection, setHasInitializedWorkflowSelection] = useState(false);

  useEffect(() => {
    if (isWorkflow && pagedData.length > 0 && !hasInitializedWorkflowSelection) {
      const initialSelection = pagedData.reduce((acc, row) => {
        acc[row.id] = true;
        return acc;
      }, {} as Record<string, boolean>);
      setRowSelection(initialSelection);
      setHasInitializedWorkflowSelection(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWorkflow, pagedData, hasInitializedWorkflowSelection]);

  if (props.mode === 'add' && props.noMembersWarning) {
    return props.noMembersWarning;
  }

  const onProjectChange = (id: number) => {
    resetMember();
    // updateSearchKey('');
    if (id) {
      setCurrentPage(1);
      const selectedProject = programs.filter((program) => program.id === id);
      markProgramsSelected(selectedProject);
      props?.fetchMembers(
        currentPage,
        pageSize,
        searchKey,
        selectedProject.map((project) => Number(project.id)),
      );
      return;
    }
    markProgramsSelected([]);
    props?.fetchMembers(currentPage, pageSize, searchKey, []);
  };

  // Function to fetch ALL members by community
  const fetchMembersByCommunity = async (communityId: number) => {
    setLoadingCommunityMembers(true);

    try {
      // Fetch large number of members to get complete list
      const result = await aspirexClient.query({
        query: GET_MEMBERS_BY_COMMUNITY_QUERY,
        variables: {
          communityId,
          skip: 0,
          take: MAX_ALLOWED_MEMBERS
        },
        fetchPolicy: 'network-only',
      });

      setCommunityMembers(result.data.members || []);
      return result.data.members || [];
    } catch (error) {
      logger.error('Error fetching community members:', error);
      setCommunityMembers([]);
      return [];
    } finally {
      setLoadingCommunityMembers(false);
    }
  };

  // Handle community selection change
  const onCommunityChange = async (id: number) => {
    setCurrentPage(1);
    if (id) {
      const selectedCommunity = communitiesData?.communities?.find((community: TCommunity) => community.id === id);

      if (selectedCommunity) {
        setSelectedCommunities({ [selectedCommunity.id]: selectedCommunity });
        setCommunityFilterActive(true);

        // Fetch ALL members for this community (not just paginated offer members)
        await fetchMembersByCommunity(selectedCommunity.id);
      }
      return;
    }

    setSelectedCommunities({});
    setCommunityFilterActive(false);
    setCommunityMembers([]);
  };

  const renderMemberCount = () => {
    // When search is active, show filtered count instead of total count
    const displayCount = searchKey ? filteredData.length : totalMember;

    return (
      <div className="flex items-center gap-2">
        <p data-testid="member-count" className="text-sm text-grey-6 m-0 font-medium">
          {displayCount || filteredData.length
            ? `${selectedMemberCount
              ? `${displayCount} Members (${selectedMemberCount} selected)`
              : `${Math.max(displayCount, filteredData.length)} Members`
            }`
            : ''}
        </p>
      </div>
    );
  };
  const selectedProgramId = !isWorkflow && selectedPrograms && Object.values(selectedPrograms).length
    ? Object.values(selectedPrograms)[0].id
    : '';

  const selectedCommunityId = !isWorkflow && selectedCommunities && Object.values(selectedCommunities).length
    ? Object.values(selectedCommunities)[0].id
    : '';

  const communities = communitiesData?.communities || [];

  if (isNull(members)) {
    return (
      <>
        <LoadSpinner />
      </>
    );
  }
  if (!isWorkflow && !pagedData.length && !loadingCommunityMembers) {
    return (
      <>
        <div className="p-4 space-y-4">
          <p className="text-sm text-grey-4 m-0 font-normal">Select the members who will receive the codes for this promotion.</p>
          <div className="flex items-center justify-between gap-4">
            <div className="flex gap-4">
              {!isWorkflow && programs?.length && (
                <div className="w-[200px]">
                  <Select
                    defaultValue={selectedProgramId?.toString() || 'all-programs'}
                    onValueChange={(value) => onProjectChange(value === 'all-programs' ? 0 : Number(value))}
                  >
                    <SelectTrigger className="w-full" data-testid="program-select-trigger">
                      <SelectValue placeholder="All Projects" />
                    </SelectTrigger>
                    <SelectContent className="z-[9999]">
                      <SelectItem value="all-programs">All Projects</SelectItem>
                      {programs
                        .map((program) => program)
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((program) => (
                          <SelectItem
                            data-testid={`program-option-${program.id}`}
                            key={program.id}
                            value={program.id.toString()}
                          >
                            {program.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {!isWorkflow && communities?.length && (
                <div className="w-[200px]">
                  <Select
                    defaultValue={selectedCommunityId?.toString() || 'all-groups'}
                    onValueChange={(value) => onCommunityChange(value === 'all-groups' ? 0 : Number(value))}
                  >
                    <SelectTrigger className="w-full" data-testid="community-select-trigger">
                      <SelectValue placeholder="All Groups" />
                    </SelectTrigger>
                    <SelectContent className="z-[9999]">
                      <SelectItem value="all-groups">All Groups</SelectItem>
                      {[...communities]
                        .sort((a: TCommunity, b: TCommunity) => a.title.localeCompare(b.title))
                        .map((community: TCommunity) => (
                          <SelectItem
                            data-testid={`community-option-${community.id}`}
                            key={community.id}
                            value={community.id.toString()}
                          >
                            {community.title}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="w-full">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  data-testid="member-search-input"
                  placeholder="Search"
                  value={searchKey}
                  onChange={(e) => onSearch(e)}
                  className="pl-8"
                  autoFocus
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            {renderMemberCount()}
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    data-testid="column-visibility-button"
                    variant="ghost"
                    size="icon"
                    disabled={isEmpty(filteredData)}
                    aria-label="Toggle column visibility"
                  >
                    <Columns className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  data-testid="column-visibility-popover"
                  align="end"
                  className="w-56 p-4 z-[9999] relative select-text pointer-events-auto"
                >
                  <div className="flex flex-col gap-2">
                    <h4 className="font-medium mb-2">Visible Columns</h4>
                    {columns.map((column: ColumnDef<IRowData> & { accessorKey: string }) => (
                      <label key={column.accessorKey} className="flex items-center gap-2">
                        <Checkbox
                          data-testid={`${column.accessorKey}-column-checkbox`}
                          checked={columnVisibility[column.accessorKey] !== false}
                          disabled={column.accessorKey === 'name'}
                          onCheckedChange={(checked) => {
                            setColumnVisibility((prev) => ({
                              ...prev,
                              [column.accessorKey]: checked === true,
                            }));
                          }}
                        />
                        <span>{column.header}</span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <TablePagination
                data-testid="pagination-text"
                pageIndex={currentPage - 1}
                pageSize={pageSize}
                totalCount={searchKey ? filteredData.length : totalMember}
                canPreviousPage={currentPage > 1}
                canNextPage={currentPage < Math.ceil((searchKey ? filteredData.length : totalMember) / pageSize)}
                onPaginationChange={(newPage) => setCurrentPage(newPage + 1)}
              />
            </div>
          </div>
          <div className="overflow-y-auto border-t border-l border-r rounded-xl">
            <DataTable
              columns={columns}
              data={[]}
              enableRowSelection
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              getRowId={(row) => row.id.toString()}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
            // pinnedLeft={['name']}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="p-4 space-y-4 overflow-y-scroll">
        {getDuplicateCodesAlert(
          communityFilterActive ?
            filteredData.map((row) => ({ ...row._raw, selected: !!rowSelection[row.id] })) :
            members
        )}
        {getSelectedMemberSizeAlert(
          communityFilterActive ?
            filteredData.map((row) => ({ ...row._raw, selected: !!rowSelection[row.id] })) :
            members,
          offerSource,
          isNewFlow
        )}
        <p className="text-sm text-grey-4 m-0 font-normal">Select the members who will receive the codes for this promotion.</p>
        <div className="flex items-center justify-between gap-4">
          <div className="flex gap-4">
            {!isWorkflow && programs?.length && (
              <div className="w-[200px]">
                <Select
                  defaultValue={selectedProgramId?.toString() || 'all-programs'}
                  onValueChange={(value) => onProjectChange(value === 'all-programs' ? 0 : Number(value))}
                >
                  <SelectTrigger className="w-full" data-testid="program-select-trigger">
                    <SelectValue placeholder="All Projects" />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="all-programs">All Projects</SelectItem>
                    {programs
                      .map((program) => program)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((program) => (
                        <SelectItem
                          data-testid={`program-option-${program.id}`}
                          key={program.id}
                          value={program.id.toString()}
                        >
                          {program.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {!isWorkflow && communities?.length && (
              <div className="w-[200px]">
                <Select
                  defaultValue={selectedCommunityId?.toString() || 'all-groups'}
                  onValueChange={(value) => onCommunityChange(value === 'all-groups' ? 0 : Number(value))}
                >
                  <SelectTrigger className="w-full" data-testid="community-select-trigger">
                    <SelectValue placeholder="All Groups" />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="all-groups">All Groups</SelectItem>
                    {[...communities]
                      .sort((a: TCommunity, b: TCommunity) => a.title.localeCompare(b.title))
                      .map((community: TCommunity) => (
                        <SelectItem
                          data-testid={`community-option-${community.id}`}
                          key={community.id}
                          value={community.id.toString()}
                        >
                          {community.title}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="w-full">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="member-search-input"
                placeholder="Search"
                value={searchKey}
                onChange={(e) => onSearch(e)}
                className="pl-8"
                autoFocus
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          {renderMemberCount()}
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  data-testid="column-visibility-button"
                  variant="ghost"
                  size="icon"
                  disabled={isEmpty(filteredData)}
                  aria-label="Toggle column visibility"
                >
                  <Columns className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                data-testid="column-visibility-popover"
                align="end"
                className="w-56 p-4 z-[9999] relative select-text pointer-events-auto"
              >
                <div className="flex flex-col gap-2">
                  <h4 className="font-medium mb-2">Visible Columns</h4>
                  {columns.map((column: ColumnDef<IRowData> & { accessorKey: string }) => (
                    <label key={column.accessorKey} className="flex items-center gap-2">
                      <Checkbox
                        data-testid={`${column.accessorKey}-column-checkbox`}
                        checked={columnVisibility[column.accessorKey] !== false}
                        disabled={column.accessorKey === 'name'}
                        onCheckedChange={(checked) => {
                          setColumnVisibility((prev) => ({
                            ...prev,
                            [column.accessorKey]: checked === true,
                          }));
                        }}
                      />
                      <span>{column.header}</span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <TablePagination
              data-testid="pagination-text"
              pageIndex={currentPage - 1}
              pageSize={pageSize}
              totalCount={searchKey ? filteredData.length : totalMember}
              canPreviousPage={currentPage > 1}
              canNextPage={currentPage < Math.ceil((searchKey ? filteredData.length : totalMember) / pageSize)}
              onPaginationChange={(newPage) => setCurrentPage(newPage + 1)}
            />
          </div>
        </div>
        <div className="overflow-y-auto border-t border-l border-r rounded-xl">
          <DataTable
            columns={columns}
            data={pagedData}
            enableRowSelection
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            getRowId={(row) => row.id.toString()}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            // pinnedLeft={['name']}
            isLoading={isEmpty(pagedData) || loadingCommunityMembers}
          />
        </div>
      </div>
    </>
  );
};
